import { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Text,
  Group,
  Grid,
  Select,
  MultiSelect,
  Paper,
  SimpleGrid,
  Skeleton,
  Alert,
  Stack,
} from '@mantine/core';
import { IconAlertCircle, IconFilter } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ReportListCard } from '../reports/ReportListCard';
import { PageHeader } from '../common/PageHeader';
import { PaginationBar } from '../common/PaginationBar';
import { getAdminReports, getStatusOptions, type AdminReportRow } from '../../api/admin';
import { AdminSubawardeeFilterSelect } from './AdminSubawardeeFilterSelect';
import { useSyncSelectedGroupFromSubawardee } from '../../hooks/useSyncSelectedGroupFromSubawardee';
import { useAdminSubawardeeUrlState } from '../../hooks/useAdminSubawardeeUrlState';
import { withAdminSubawardeeQuery } from '../../utils/adminSubawardeePaths';
import { useAdminComponentProjectFilterStore } from '../../stores/adminComponentProjectFilterStore';
import { useAdminInitiativeLeadsQuery } from '../../hooks/useAdminInitiativeLeadsQuery';
import { useGroup } from '../../contexts/GroupContext';
import { buildComponentProjectIdFilterParam } from '../../config/componentProjects';
import { submissionStateTokenToLabel } from '../../types/status';
import type { QuarterlyReport, ReportStatus } from '../../types/reports';
import {
  buildSubawardeeLabelLookup,
  filterSubawardeeIds,
  getSubawardeeDisplayLabel,
  subawardeeBelongsToComponentProject,
} from '../../utils/adminSubawardeeUtils';

const PAGE_SIZE = 24;

const filterInputStyles = (theme: any) => {
  const focusBorder = theme.colorScheme === 'dark' ? theme.colors.dark[3] : theme.colors.gray[5];
  return {
    label: { fontSize: theme.fontSizes.xs, fontWeight: 500, marginBottom: 4 },
    input: {
      '&:focus, &:focus-within': { borderColor: focusBorder, boxShadow: 'none', outline: 'none' },
    },
    searchInput: {
      '&:focus': { outline: 'none', boxShadow: 'none' },
    },
  };
};

const FALLBACK_STATUS_OPTIONS = [
  { value: 'not_submitted', label: 'Not Submitted' },
  { value: 'pending_review', label: 'Pending Review' },
  { value: 'in_review', label: 'In Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

function buildYearSelectOptions(pageYears: string[]): { value: string; label: string }[] {
  const currentYear = dayjs().year();
  const windowYears = Array.from({ length: 6 }, (_, i) => String(currentYear + 1 - i));
  return Array.from(new Set([...windowYears, ...pageYears]))
    .sort((a, b) => b.localeCompare(a))
    .map((year) => ({ value: year, label: year }));
}

function mapAdminReportRowToQuarterlyReport(
  row: AdminReportRow,
  includeSubawardeeInTitle: boolean,
  subawardeeLabel?: string | null
): QuarterlyReport {
  const periodLabel = row.reporting_period_label?.trim() || 'Report';
  // Match subawardee report cards: prefer report_title, then report_name, then period label.
  const reportTitle =
    row.report_title?.trim() || row.report_name?.trim() || periodLabel;
  const label = subawardeeLabel?.trim() || row.subawardee;
  // Cross-org view: subawardee as primary title, report name as secondary line.
  const useSubawardeePrimary = includeSubawardeeInTitle && Boolean(label);

  return {
    id: row.report_id,
    name: row.report_name?.trim() || periodLabel,
    title: useSubawardeePrimary ? label! : reportTitle,
    ...(useSubawardeePrimary ? { subtitle: reportTitle } : {}),
    dueDate: row.due_date,
    status: (row.submission_status?.state ?? 'not_submitted').toLowerCase() as ReportStatus,
    subawardee: row.subawardee,
    reportingYear: row.reporting_year,
  };
}

export function AdminReportsPage() {
  const navigate = useNavigate();
  const [subawardee, setSubawardee] = useAdminSubawardeeUrlState();
  const componentProject = useAdminComponentProjectFilterStore((s) => s.componentProject);
  const { data: initiativeLeads = [] } = useAdminInitiativeLeadsQuery();
  const { availableGroups } = useGroup();
  useSyncSelectedGroupFromSubawardee(subawardee);
  const [selectedYear, setSelectedYear] = useState<string | null>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [page, setPage] = useState(1);

  const labelById = useMemo(
    () => buildSubawardeeLabelLookup(availableGroups),
    [availableGroups]
  );

  const subawardeeScopeIds = useMemo(
    () => filterSubawardeeIds(initiativeLeads, { componentProjectId: componentProject }),
    [initiativeLeads, componentProject]
  );

  const subawardeeSelectData = useMemo(
    () =>
      subawardeeScopeIds
        .map((id) => ({
          value: id,
          label: getSubawardeeDisplayLabel(id, labelById) ?? id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [subawardeeScopeIds, labelById]
  );

  const showAllSubawardees = !subawardee;
  const componentProjectIdQueryParam = useMemo(
    () => buildComponentProjectIdFilterParam(componentProject),
    [componentProject]
  );

  const statusQueryParam = useMemo(() => {
    if (selectedStatuses.length === 0) return undefined;
    return selectedStatuses
      .map((s) => s.trim())
      .filter(Boolean)
      .join(',');
  }, [selectedStatuses]);

  useEffect(() => {
    setSelectedYear(null);
    setSelectedStatuses([]);
    setPage(1);
  }, [subawardee, componentProject]);

  useEffect(() => {
    setPage(1);
  }, [selectedYear, statusQueryParam]);

  useEffect(() => {
    if (!componentProject || !subawardee) return;
    if (!subawardeeBelongsToComponentProject(subawardee, componentProject, initiativeLeads)) {
      setSubawardee(null);
    }
  }, [componentProject, subawardee, setSubawardee, initiativeLeads]);

  const statusOptionsQueryKey = useMemo(() => {
    if (subawardee) return subawardee;
    if (!componentProject) return 'all';
    return subawardeeScopeIds.join(',');
  }, [subawardee, componentProject, subawardeeScopeIds]);

  const { data: statusOptionsData } = useQuery({
    queryKey: ['admin-status-options', 'reports-page', statusOptionsQueryKey],
    queryFn: () =>
      getStatusOptions({
        ...(subawardee
          ? { subawardee }
          : !componentProject
            ? {}
            : subawardeeScopeIds.length > 0
              ? { subawardee: subawardeeScopeIds.join(',') }
              : {}),
      }),
    staleTime: 10 * 60 * 1000,
  });

  const reportsQueryEnabled = Boolean(subawardee) || subawardeeScopeIds.length > 0;

  const {
    data: reportsPage,
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: [
      'admin-reports',
      componentProject,
      subawardee,
      selectedYear,
      statusQueryParam,
      page,
      PAGE_SIZE,
    ],
    queryFn: async () => {
      const response = await getAdminReports({
        page,
        page_size: PAGE_SIZE,
        sort: 'due_date-desc',
        ...(componentProjectIdQueryParam && {
          component_project_id: componentProjectIdQueryParam,
        }),
        ...(subawardee && { subawardee }),
        ...(selectedYear && { reporting_year: Number(selectedYear) }),
        ...(statusQueryParam && { status: statusQueryParam }),
      });

      const reports = (response.items ?? []).map((row) =>
        mapAdminReportRowToQuarterlyReport(
          row,
          showAllSubawardees,
          getSubawardeeDisplayLabel(row.subawardee, labelById)
        )
      );

      return {
        reports,
        pagination: response.pagination,
      };
    },
    enabled: reportsQueryEnabled,
    staleTime: 2 * 60 * 1000,
    placeholderData: (previousData) => previousData,
  });

  const reports = useMemo(() => reportsPage?.reports ?? [], [reportsPage?.reports]);
  const pagination = reportsPage?.pagination;
  const totalPages = Math.max(pagination?.total_pages ?? 1, 1);
  const totalItems = pagination?.total_items ?? 0;
  const rangeStart = totalItems === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = totalItems === 0 ? 0 : Math.min(page * PAGE_SIZE, totalItems);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const availableYears = useMemo(() => {
    const pageYears = reports
      .map((report) =>
        report.reportingYear != null
          ? String(report.reportingYear)
          : dayjs(report.dueDate).format('YYYY')
      )
      .filter(Boolean);
    return buildYearSelectOptions(pageYears);
  }, [reports]);

  const statusOptions = useMemo(() => {
    const fromApi = (statusOptionsData?.report_submission_statuses ?? []).map((value) => ({
      value,
      label: submissionStateTokenToLabel(value),
    }));
    return fromApi.length > 0 ? fromApi : FALLBACK_STATUS_OPTIONS;
  }, [statusOptionsData]);

  const secondaryFiltersDisabled = isLoading && !reportsPage;
  const showInitialSkeleton = isLoading && !reportsPage;

  const openReportInAdminViewOnly = (reportId: string, reportSubawardee?: string): void => {
    navigate(
      withAdminSubawardeeQuery(`/admin/reports/${reportId}`, reportSubawardee ?? subawardee)
    );
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Container size="xl" mt="xl" pb={80} sx={{ minHeight: 'calc(100vh - 140px)' }} px={0}>
      <PageHeader
        title="Reports"
        subtitle="View quarterly reports across sub-awardees. Narrow by sub-awardee, year, or status."
      />

      <Paper
        radius="md"
        p="md"
        mb="lg"
        sx={(theme) => ({
          backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[0],
          border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]}`,
          '@media (max-width: 768px)': {
            margin: theme.spacing.sm,
            padding: theme.spacing.sm,
          },
        })}
      >
        <Group spacing={6} mb="sm">
          <IconFilter size={15} style={{ opacity: 0.55 }} />
          <Text size="xs" weight={600} tt="uppercase" color="dimmed" sx={{ letterSpacing: 0.5 }}>
            Filters
          </Text>
        </Group>

        <Grid gutter="sm">
          <Grid.Col span={4} xs={12} sm={4}>
            <AdminSubawardeeFilterSelect
              value={subawardee}
              onChange={setSubawardee}
              data={subawardeeSelectData}
              disabled={isFetching && !reportsPage}
              style={undefined}
              styles={filterInputStyles}
              size="sm"
            />
          </Grid.Col>
          <Grid.Col span={4} xs={12} sm={4}>
            <Select
              label="Year"
              placeholder="All years"
              value={selectedYear}
              onChange={setSelectedYear}
              clearable
              data={availableYears}
              disabled={secondaryFiltersDisabled}
              size="sm"
              styles={filterInputStyles}
            />
          </Grid.Col>
          <Grid.Col span={4} xs={12} sm={4}>
            <MultiSelect
              label="Status"
              placeholder="All statuses"
              value={selectedStatuses}
              onChange={setSelectedStatuses}
              clearable
              data={statusOptions}
              disabled={secondaryFiltersDisabled}
              size="sm"
              styles={filterInputStyles}
            />
          </Grid.Col>
        </Grid>
      </Paper>

      {!reportsQueryEnabled ? (
        <Alert color="blue" mb="xl">
          No sub-awardees are available for the current filters.
        </Alert>
      ) : null}

      {error ? (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error"
          color="red"
          role="alert"
          sx={(theme) => ({
            '@media (max-width: 768px)': {
              margin: theme.spacing.sm,
            },
          })}
        >
          Failed to load reports. Please try again later.
        </Alert>
      ) : null}

      {reportsQueryEnabled ? (
        <>
          <SimpleGrid
            cols={3}
            spacing="lg"
            breakpoints={[
              { maxWidth: 'md', cols: 2 },
              { maxWidth: 'sm', cols: 1 },
              { maxWidth: 480, cols: 1, spacing: 'md' },
            ]}
            sx={(theme) => ({
              opacity: isFetching && !showInitialSkeleton ? 0.7 : 1,
              transition: 'opacity 0.15s ease',
              '@media (max-width: 768px)': {
                margin: theme.spacing.sm,
              },
            })}
          >
            {showInitialSkeleton ? (
              Array(6)
                .fill(0)
                .map((_, index) => (
                  <Paper key={index} p="md" withBorder>
                    <Stack spacing="md">
                      <Skeleton height={24} width="70%" radius="md" />
                      <Skeleton height={20} width="40%" radius="md" />
                      <Group
                        position="apart"
                        mt="md"
                        sx={(theme) => ({
                          '@media (max-width: 480px)': {
                            flexDirection: 'column',
                            gap: theme.spacing.sm,
                          },
                        })}
                      >
                        <Skeleton height={32} width={100} radius="md" />
                        <Skeleton height={32} width={100} radius="md" />
                      </Group>
                    </Stack>
                  </Paper>
                ))
            ) : error ? null : reports.length > 0 ? (
              reports.map((report) => (
                <ReportListCard
                  key={report.subawardee ? `${report.subawardee}:${report.id}` : report.id}
                  report={report}
                  viewOnly
                  onSubmit={(id) => openReportInAdminViewOnly(id, report.subawardee)}
                  onView={(id) => openReportInAdminViewOnly(id, report.subawardee)}
                  onUpdate={(id) => openReportInAdminViewOnly(id, report.subawardee)}
                />
              ))
            ) : (
              <Text align="center" color="dimmed" style={{ gridColumn: '1 / -1' }}>
                No reports match the selected filters.
              </Text>
            )}
          </SimpleGrid>

          {!error && totalItems > 0 && totalPages > 1 ? (
            <PaginationBar
              page={page}
              totalPages={totalPages}
              totalItems={totalItems}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              disabled={isFetching}
              onChange={handlePageChange}
            />
          ) : null}
        </>
      ) : null}
    </Container>
  );
}
